import apiV2 from '../api/v2';
import type { WorkQueueCase, PriorityCounts } from './workQueue';
import type { ConversationRow } from './conversations';

// Admin home dashboard data, split by cost: /team-stats is BigQuery-backed and
// server-cached, the two card endpoints are live Postgres reads.

/** Bucket width the server chose for the series, derived from the range. */
export type TeamStatsBucket = 'day' | 'week' | 'month';

/** Mirrors OwnerTeam on the backend (services/workflow/teams.ts). */
export type OwnerTeam = 'Admin' | 'Clinical' | 'Ortho' | 'DM' | 'TC' | 'GP';

export interface TeamSeriesPoint {
    bucket: string; // ISO date at the start of the bucket
    team: OwnerTeam;
    inflow: number;
    throughput: number;
}

export interface TeamBacklog {
    team: OwnerTeam;
    open: number;
}

/** Reconstructed historical open-case count — see the backend's buildBacklogSeries doc. */
export interface TeamBacklogPoint {
    bucket: string;
    team: OwnerTeam;
    open: number;
}

export type CaseAgeBucket = 'under1d' | '1to3d' | '3to7d' | 'over7d';

/** Open cases by time in their current step; every team gets all four buckets, in order. */
export interface TeamAgeBucket {
    team: OwnerTeam;
    bucket: CaseAgeBucket;
    open: number;
}

/** Open DM monitoring items right now, the same numbers as /monitoring/overview. */
export interface TeamMonitoring {
    notifications: number;
    messages: number;
}

/** Live open cases per Work Queue step; open > 0 only, ordered by team then open desc. */
export interface TeamStepBacklog {
    team: OwnerTeam;
    statusId: string;
    label: string;
    open: number;
}

export interface CaseVersionPoint {
    bucket: string;
    v1: number;
    v2: number;
}

/** Non-test cases by submission or approval date, split by product version. */
export interface CaseVersionKpi {
    total: number;
    v1: number;
    v2: number;
    series: CaseVersionPoint[];
}

export interface CaseCountPoint {
    bucket: string;
    count: number;
}

export interface CaseDeclinedKpi {
    total: number;
    series: CaseCountPoint[];
}

/** Series are zero-filled across the range, one point per response bucket. */
export interface CaseKpis {
    submitted: CaseVersionKpi;
    approved: CaseVersionKpi;
    /** Null when BigQuery is unavailable. */
    declined: CaseDeclinedKpi | null;
}

export interface ConversationOpenPoint {
    bucket: string;
    open: number;
}

/** Practice conversations in and treated by ODO; Postgres-backed, so always present. */
export interface ConversationKpis {
    received: number;
    treated: number;
    /** Null when nothing was replied to in the range. */
    medianFirstReplyMinutes: number | null;
    /** Waiting for ODO now: not replied to and not marked read. */
    openNow: number;
    /** Open at the end of each bucket; the last point equals openNow. */
    openSeries: ConversationOpenPoint[];
    /** Read events weren't tracked before this date, so earlier open counts overstate. */
    openReliableFrom: string | null;
}

export interface TeamStats {
    bucket: TeamStatsBucket;
    /** False when the environment has no BIGQUERY_* config, so only backlog and age resolve. */
    flowAvailable: boolean;
    series: TeamSeriesPoint[];
    backlog: TeamBacklog[];
    backlogSeries: TeamBacklogPoint[];
    ageBuckets: TeamAgeBucket[];
    stepBacklog: TeamStepBacklog[];
    monitoring: TeamMonitoring;
    caseKpis: CaseKpis;
    conversationKpis: ConversationKpis;
}

export interface MyCases {
    cases: WorkQueueCase[];
    /** Every case assigned to me, not just the returned page. */
    total: number;
    priorityCounts: PriorityCounts;
}

export async function fetchTeamStats(from: string | null, to: string): Promise<TeamStats> {
    const response = await apiV2.get<{ success: true; data: TeamStats }>('/admin/home/team-stats', {
        params: from ? { from, to } : { to }
    });
    return response.data.data;
}

export interface MyConversations {
    conversations: ConversationRow[];
}

export async function fetchMyConversations(limit: number): Promise<MyConversations> {
    const response = await apiV2.get<{ success: true; data: MyConversations }>(
        '/admin/home/my-conversations',
        { params: { limit } }
    );
    return response.data.data;
}

export async function fetchMyCases(limit: number): Promise<MyCases> {
    const response = await apiV2.get<{ success: true; data: MyCases }>('/admin/home/my-cases', {
        params: { limit }
    });
    return response.data.data;
}
